import { useState, useCallback } from 'react';
import {
  handleSubmitMultipleMatches as matchSubmitMultiple,
  handleVerifyMatch as matchVerify,
  loadMatches,
} from '../services/api/matchService';
import { logger } from '../utils/logger';
import { isDemoMode } from '../utils/demoUtils';
import { mockDB } from '../services/mock/mockData';

const useMockData = false; // Use API data

export function useMatchData(userProfile, players = [], setPlayers = null) {
  const [matches, setMatches] = useState([]);
  const [formError, setFormError] = useState('');

  // Load matches data
  const loadMatchesData = useCallback(async () => {
    // Demo Mode Logic
    if (isDemoMode()) {
      logger.debug('Demo Mode: Loading mock matches');
      const matches = mockDB.getMatches();
      setMatches(matches);
      return matches;
    }

    if (useMockData) return;

    try {
      const matchesResult = await loadMatches();
      setMatches(matchesResult);
      return matchesResult;
    } catch (error) {
      logger.error('Error loading matches data:', error);
      throw error;
    }
  }, []);

  /**
   * Validates a single match object
   * @param {Object} match - Match object with t1p1, t1p2, t2p1, t2p2, score1, score2
   * @param {number} index - Index for error messages
   * @returns {Object} Validation result
   */
  const validateSingleMatch = (match, index) => {
    const { t1p1, t1p2, t2p1, t2p2, score1, score2 } = match;
    const matchLabel = `Match ${index + 1}`;

    // Check for 4 unique players
    const allPlayers = [t1p1, t1p2, t2p1, t2p2].filter(Boolean);
    if (new Set(allPlayers).size !== 4) {
      return {
        isValid: false,
        error: `${matchLabel}: You must select 4 unique players.`,
      };
    }

    // Validate scores are numbers
    const parsedScore1 = parseInt(score1);
    const parsedScore2 = parseInt(score2);

    if (isNaN(parsedScore1) || isNaN(parsedScore2)) {
      return {
        isValid: false,
        error: `${matchLabel}: Please enter valid scores.`,
      };
    }

    // Validate game rules (one team must win with exactly 10)
    const isTeam1Win = parsedScore1 === 10 && parsedScore2 < 10;
    const isTeam2Win = parsedScore2 === 10 && parsedScore1 < 10;

    if (!isTeam1Win && !isTeam2Win) {
      return {
        isValid: false,
        error: `${matchLabel}: Invalid Score. One team must win with exactly 10 goals.`,
      };
    }

    return {
      isValid: true,
      error: null,
      data: {
        players: { t1p1, t1p2, t2p1, t2p2 },
        scores: { score1: parsedScore1, score2: parsedScore2 },
      },
    };
  };

  // Match operations - always handles array of matches
  const handleSubmitMatch = async (e, matchesData) => {
    e.preventDefault();
    setFormError('');

    if (isDemoMode()) {
      const newMatches = [];

      for (let i = 0; i < matchesData.length; i++) {
        const validation = validateSingleMatch(matchesData[i], i);
        if (!validation.isValid) {
          logger.debug('Demo Mode: Match validation failed:', validation.error);
          setFormError(validation.error);
          throw new Error(validation.error);
        }

        const { t1p1, t1p2, t2p1, t2p2 } = validation.data.players;
        const { score1, score2 } = validation.data.scores;
        const matchTime = matchesData[i].matchTime;

        // Convert matchTime from local browser time to UTC ISO string
        let timestamp;
        if (matchTime) {
          timestamp = new Date(matchTime).toISOString();
        } else {
          timestamp = new Date().toISOString();
        }

        const matchData = {
          team1Players: [t1p1, t1p2],
          team2Players: [t2p1, t2p2],
          team1Score: score1,
          team2Score: score2,
          winners: score1 > score2 ? [t1p1, t1p2] : [t2p1, t2p2],
          losers: score1 > score2 ? [t2p1, t2p2] : [t1p1, t1p2],
          score: `${score1}-${score2}`,
          matchTime,
          timestamp,
          submittedByName: userProfile?.name || 'Unknown',
          submittedByEmail: userProfile?.email || 'unknown@office-arena.local'
        };

        const newMatch = mockDB.addMatch(matchData);
        newMatches.push(newMatch);
      }

      setMatches(prev => [...newMatches.reverse(), ...prev]);
      return;
    }

    // API Mode - always use multi-match handler
    await matchSubmitMultiple(
      e,
      matchesData,
      userProfile,
      matches,
      setMatches,
      undefined,
      setFormError,
      players,
      setPlayers
    );
  };

  const handleVerifyMatch = async matchId => {
    await matchVerify(
      matchId,
      userProfile,
      matches,
      setMatches,
      players,
      setPlayers
    );
  };

  return {
    // State
    matches,
    formError,
    setMatches,
    setFormError,

    // Operations
    loadMatchesData,
    handleSubmitMatch,
    handleVerifyMatch,
  };
}
